/**
 * useAudioManager — Feb 2026
 * ===========================
 * Centralised hook for any component that plays HTMLAudioElement instances
 * (OpenAI TTS, ElevenLabs, etc). Solves the recurring "voice keeps playing
 * after the user navigates away" class of bugs by:
 *
 *   1. Tracking the active <audio> in a ref so cleanup always silences it.
 *   2. Exposing an `isMounted()` predicate that callers MUST check after
 *      every `await` so an in-flight playback aborts if the component
 *      unmounted (Back button, programmatic navigate, route change).
 *   3. Auto-registering `beforeunload` / `pagehide` listeners so tab close
 *      and hard refresh also kill audio.
 *   4. Cancelling `window.speechSynthesis` on unmount as a belt-and-braces
 *      measure for components that fall back to native TTS.
 *
 * Usage:
 *   const audio = useAudioManager();
 *   ...
 *   const el = new Audio(src);
 *   if (!audio.attach(el)) return;  // unmounted between fetch + here
 *   await el.play();
 *   if (!audio.isMounted()) return; // unmounted during play
 */
import { useRef, useEffect, useCallback } from 'react';

export const useAudioManager = () => {
  const audioRef = useRef(null);
  const mountedRef = useRef(true);

  const stop = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current.ontimeupdate = null;
        audioRef.current.src = '';
        audioRef.current.load();
      } catch (e) { /* element already detached */ }
      audioRef.current = null;
    }
    try { window.speechSynthesis?.cancel(); } catch (e) { /* unsupported */ }
  }, []);

  /**
   * Attach a freshly-constructed HTMLAudioElement. Stops any previous
   * audio first. Returns `false` if the component has already unmounted
   * (caller should bail and not call `.play()`).
   */
  const attach = useCallback((audio) => {
    if (!mountedRef.current) {
      try { audio.pause(); audio.src = ''; } catch (e) { /* ignore */ }
      return false;
    }
    stop();
    audioRef.current = audio;
    return true;
  }, [stop]);

  const isMounted = useCallback(() => mountedRef.current, []);

  useEffect(() => {
    mountedRef.current = true;
    const handlePageHide = () => stop();
    window.addEventListener('beforeunload', handlePageHide);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('beforeunload', handlePageHide);
      window.removeEventListener('pagehide', handlePageHide);
      stop();
    };
  }, [stop]);

  return { audioRef, attach, stop, isMounted };
};

export default useAudioManager;
